import { useState } from 'react';

import {
    RegisterUserDocument,
    RegisterUserMutation,
    RegisterUserMutationVariables
} from '../../queries/registerUser.generated';

import RegistrationFormData, {
    RegisterMutationData
} from '../interfaces/RegistrationFormData';
import { triggerSuccessfulAuthentication } from '../utils/triggerSuccessfulAuthentication';
import { FetchUserDocument, FetchUserQuery } from '@zoopla/user-queries';
import { UseFormSetError } from 'react-hook-form';
import { fetchGraphql, useLazyFetchGraphql } from '@zoopla/fetch-graphql';
import { logError } from '@zoopla/logger';

const errorCodes: { [key: string]: string } = {
    EMAIL_EXISTS: 'This email address is already in use',
    EMAIL_DELETED: 'This email address is already in use',
    USERNAME_EXISTS: 'That username already exists',
    PASSWORDS_DONT_MATCH: 'The passwords you entered do not match',
    BAD_EMAIL_FORMAT: 'The email address you entered is invalid',
    CONTACTNAME_MISSING_OR_INVALID: 'That contact name is missing or invalid',
    CONTAINS_PROFANITY: 'This username is not available',
    INCORRECT_EMAIL_PASSWORD:
        'Incorrect email or password. Please check and try again',
    GOOGLE_CLOSE_WINDOW_ERROR:
        'Something went wrong because the window was closed too soon. Please try again if you would like to continue.',
    GENERIC_SOCIAL_SIGN_IN_ERROR:
        'Something went wrong and you have not been signed in. Please try again.'
};

const useRegistration = (setError: UseFormSetError<RegistrationFormData>) => {
    const [generalError, setGeneralError] = useState(false);

    // this function is used to fetch the user after the registration has succeded
    // by the components using it
    const getUserData = () => fetchGraphql<FetchUserQuery>(FetchUserDocument);

    const { lazyFetch: registerMutation } = useLazyFetchGraphql<
        RegisterUserMutation,
        RegisterUserMutationVariables
    >(RegisterUserDocument, {
        onCompleted({ registerUser: response }) {
            if (response?.errorField && response.errorCode) {
                const message =
                    errorCodes[response.errorCode] ??
                    'Sorry, something went wrong. Please try again.';

                setError(
                    response.errorField as keyof RegistrationFormData,
                    {
                        type: 'manual',
                        message
                    },
                    { shouldFocus: true }
                );
                return;
            }
            if (!response?.success) {
                setGeneralError(true);
                return;
            }

            triggerSuccessfulAuthentication();
        },
        onError(error) {
            logError(error);
            setGeneralError(true);
        }
    });

    const registerUser = (props: RegisterMutationData) => {
        setGeneralError(false);

        const { email, password, situation, consent__insights__24 } = props;

        const input = {
            email,
            password,
            propertyStatus: situation,
            consents: [
                {
                    consentName: 'consent.insights.24',
                    consentGiven: consent__insights__24
                }
            ]
        };

        return registerMutation({ input });
    };

    const resetGeneralError = () => {
        setGeneralError(false);
    };

    return {
        registerUser,
        generalError,
        resetGeneralError,
        getUserData
    };
};

export default useRegistration;
